import classNames from "classnames";
import PropTypes from "prop-types";
import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslate } from "../../../language/i18n";
import { ADVANCE_CONTACT_OPTION } from "../../../utils/constants";
import { getFullDate } from "../../../utils/DateUtil";
import { formatPostalNo } from "../../../utils/postalNo";
import Label from "../../shared/Label/Label";
import RadioButtonGroup from "../RadioButton/RadioButtonGroup";
import styles from "./RecieverInformation.module.scss";

const RecieverInformation = ({ customCssClass, receiverData, title }) => {
  const translate = useTranslate();

  let receiver = useSelector(state => state.appData.order?.receiver);

  if (receiverData) {
    receiver = receiverData;
  }

  if (!receiver) {
    return null;
  }

  const FacilityInformation = () => {
    const address = receiver?.address;
    return (
      <>
        <h2 className={styles.title}>{translate("INFORMATION_ABOUT_FACILITY")}</h2>
        <Label label={translate("BASE_STORAGE")} value={`${receiver?.value} - ${receiver?.label}`} />
        <p className={styles.text}>
          <span className={styles.label}>{translate("ADDRESS")}:</span>
          {address?.street && <span>{address.street}</span>}
          {address?.postalNo && <span className={styles.inline}>{formatPostalNo(address.postalNo)}</span>}
          {address?.city && <span className={styles.inline}>{address.city}</span>}
        </p>
      </>
    );
  };

  return (
    <aside className={classNames(styles.container, customCssClass)}>
      {receiver?.type === "FACILITY" ? <FacilityInformation /> : <UserInformation receiver={receiver} title={title} />}
    </aside>
  );
};

const UserInformation = ({ receiver, title, showDeregistrationReason }) => {
  const translate = useTranslate();
  const contact = receiver?.contactInformation;
  const address = receiver?.defaultAddress;

  return (
    <>
      <h3 className={styles.title}>{title || translate("CLIENT")}</h3>
      <div className={styles.content}>
        <div>
          <Label label={translate("FIRST_NAME")} value={receiver?.firstName} />
          <Label label={translate("LAST_NAME")} value={receiver?.surname} />
          <Label label={translate("PERSONAL_IDENTITY_NUMBER_OR_UNIT")} value={receiver?.identityNumber} />
          <Label label={translate("MUNICIPALITY_AFFILIATION")} value={receiver?.municipalAffiliation?.name} />
          <p className={styles.text}>
            <span className={styles.label}>{translate("PLACE_OF_RESIDENCE")}:</span>
            {address?.careOf && <span>{address.careOf}</span>}
            {address?.street && <span>{address.street}</span>}
            {address?.postalNo && <span className={styles.inline}>{formatPostalNo(address.postalNo)}</span>}
            {address?.city && <span className={styles.inline}>{address.city}</span>}
          </p>
          <Label label={translate("PHONE_NO")} value={contact?.phoneNo} />
          <Label label={translate("EMAIL")} value={contact?.email} />
        </div>
        {showDeregistrationReason && receiver?.deregistrationDate && (
          <div>
            <Label label={translate("DEREGISTRATION_DATE")} value={getFullDate(receiver.deregistrationDate)} />
            <Label label={translate("DEREGISTRATION_REASON")} value={receiver?.deregistrationReason?.label} />
          </div>
        )}
      </div>
    </>
  );
};

const ContactInformation = ({ contact, title }) => {
  const translate = useTranslate();
  return (
    <>
      <h3 className={styles.title}>{title || translate("CONTACT_PERSON")}</h3>
      <Label label={translate("FIRST_NAME")} value={contact?.firstName} />
      <Label label={translate("LAST_NAME")} value={contact?.surname} />
      {contact?.phoneNo && <Label label={translate("PHONE_NO")} value={contact?.phoneNo} />}
      {contact?.email && <Label label={translate("EMAIL")} value={contact?.email} />}
    </>
  );
};

const AdvanceContactInformation = ({ onChange: onChangeProp, shouldContact, showRequired = false, disabled }) => {
  const translate = useTranslate();
  const advanceContactValues = useMemo(
    () => [
      { id: ADVANCE_CONTACT_OPTION.YES, value: translate("YES") },
      { id: ADVANCE_CONTACT_OPTION.NO, value: translate("NO") },
    ],
    [translate],
  );
  const onChange = useCallback(value => onChangeProp(value.id === ADVANCE_CONTACT_OPTION.YES), [onChangeProp]);

  return (
    <>
      {showRequired ? <p className={styles.errorMessage}>{translate("MUST_SELECT_USE_ADVANCE_CONTACT")}</p> : null}
      <RadioButtonGroup
        label={translate("SHOULD_CONTACT_BEFORE_VISIT") + " *"}
        values={advanceContactValues}
        selected={
          shouldContact != null ? (shouldContact ? ADVANCE_CONTACT_OPTION.YES : ADVANCE_CONTACT_OPTION.NO) : null
        }
        onChange={onChange}
        disabled={disabled}
      />
      <div className={styles.containerMargin}>
        <p className={styles.text}>{translate("ADVANCE_CONTACT_INFO_TEXT")}</p>
      </div>
    </>
  );
};

UserInformation.propTypes = {
  title: PropTypes.string,
  receiver: PropTypes.object,
  showDeregistrationReason: PropTypes.bool,
};

ContactInformation.propTypes = {
  contact: PropTypes.object,
  title: PropTypes.string,
};

RecieverInformation.propTypes = {
  title: PropTypes.string,
  customCssClass: PropTypes.string,
  receiverData: PropTypes.object,
};

AdvanceContactInformation.propTypes = {
  shouldContact: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  showRequired: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default RecieverInformation;
export { AdvanceContactInformation, ContactInformation, Label, UserInformation };
